// src/pages/TemplateForm.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../utils/api";
import { motion } from "framer-motion";
import { FaEye, FaPrint, FaRobot, FaPlusCircle, FaTimes } from "react-icons/fa";

// 🧠 استخراج المتغيرات من النص {{...}}
const extractVariables = (text) => {
  const matches = text?.match(/{{\s*[\u0621-\u064A\w_]+\s*}}/g) || [];
  return matches.map((m) => m.replace(/[{}]/g, "").trim());
};

// ✅ استبدال المتغيرات بالقيم داخل bodyText
const applyVariables = (bodyText, formData) => {
  let body = bodyText || "";
  for (const [key, value] of Object.entries(formData || {})) {
    const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");
    body = body.replace(regex, value?.trim() ? value : "………………");
  }
  return body;
};

function TemplateForm() {
  const { id } = useParams();

  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [variables, setVariables] = useState([]);

  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const [showPreview, setShowPreview] = useState(false);
  const [previewHTML, setPreviewHTML] = useState("");

  const [aiPrompt, setAiPrompt] = useState("");

  // ✅ تحميل النموذج
  const fetchTemplate = async () => {
    try {
      const res = await api.get(`/templates/${id}`);
      const t = res.data;

      setTemplate(t);

      const vars = extractVariables(t?.bodyText || "");
      setVariables(vars);

      // init values
      const initData = {};
      vars.forEach((v) => (initData[v] = ""));
      setFormData(initData);
    } catch (err) {
      console.error(err);
      alert("❌ حدث خطأ أثناء تحميل بيانات النموذج");
    }
  };

  useEffect(() => {
    if (id) fetchTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ✅ تعديل القيم
  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // ➕ إضافة متغير يدوي
  const addVariable = () => {
    const name = prompt("أدخل اسم المتغير الجديد (مثال: clientName):");
    if (!name) return;

    const cleaned = name.trim();
    if (!cleaned) return;

    if (variables.includes(cleaned)) return alert("⚠️ المتغير موجود بالفعل");

    setVariables((prev) => [...prev, cleaned]);
    setFormData((prev) => ({ ...prev, [cleaned]: "" }));

    alert("✅ تم إضافة المتغير بنجاح");
  };

  // ✅ ستايل HTML المعاينة (SideBox LEFT)
  const buildStyledPreviewHTML = useMemo(() => {
    if (!template?.bodyText) return "";

    const body = applyVariables(template.bodyText, formData);

    // ✅ المستطيل في الشمال = flex-direction: row-reverse
    return `
      <div style="
        direction: rtl;
        font-family: 'Cairo', serif;
        color: #111;
        background: #fff;
        padding: 18mm;
        border: 1px solid #000;
        line-height: 2;
        width: 210mm;
        min-height: 297mm;
        box-sizing: border-box;
      ">

        <h2 style="
          text-align:center;
          font-weight:700;
          margin:0 0 18px 0;
          padding-bottom:10px;
          border-bottom:2px solid #000;
          font-size:22px;
        ">
          ${template.name}
        </h2>

        <div style="text-align:center; font-size:13px; color:#555; margin-bottom:18px;">
          جمهورية مصر العربية — بتاريخ ${new Date().toLocaleDateString("ar-EG")}
        </div>

        <div style="
          display:flex;
          gap:20px;
          align-items:flex-start;
          flex-direction: row-reverse; /* ✅ الصندوق في الشمال */
        ">

          <!-- ✅ الصندوق الجانبي -->
          <div style="
            width:28%;
            border:2px solid #000;
            padding:14px;
            font-size:14px;
            box-sizing:border-box;
          ">
            <p style="margin:0 0 10px"><strong>الموضوع</strong><br/>${template.name}</p>
            <hr style="border:none;border-top:1px solid #000;margin:10px 0"/>
            <p style="margin:0 0 10px"><strong>التاريخ</strong><br/>${new Date().toLocaleDateString(
              "ar-EG"
            )}</p>
            <hr style="border:none;border-top:1px solid #000;margin:10px 0"/>
            <p style="margin:0 0 10px"><strong>رقم الدعوى</strong><br/>………………</p>
            <hr style="border:none;border-top:1px solid #000;margin:10px 0"/>
            <p style="margin:0"><strong>الدائرة</strong><br/>………………</p>
          </div>

          <!-- ✅ متن العقد -->
          <div style="
            width:72%;
            font-size:16px;
            text-align:justify;
            white-space:pre-line;
            word-break:break-word;
          ">
            ${body}
          </div>
        </div>

        <div style="margin-top:60px; display:flex; justify-content:space-between; gap:20px;">
          <div style="width:45%;">
            <strong>توقيع الطرف الأول</strong>
            <div style="border-top:1px dotted #000; margin-top:30px;"></div>
          </div>
          <div style="width:45%;">
            <strong>توقيع الطرف الثاني</strong>
            <div style="border-top:1px dotted #000; margin-top:30px;"></div>
          </div>
        </div>

      </div>
    `;
  }, [template, formData]);

  // 🔍 معاينة العقد
  const previewContract = () => {
    if (!template?.bodyText) return;

    setPreviewHTML(buildStyledPreviewHTML);
    setShowPreview(true);

    // ✅ نزول للمعاينة
    setTimeout(() => {
      const el = document.getElementById("previewSection");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
  };

  // 📄 توليد PDF (يطبع نفس HTML اللي اتعدل)
  const generatePDF = async () => {
    try {
      if (!template?._id) return;

      setLoading(true);

      // ✅ هنا الحل النهائي:
      // ناخد HTML الحقيقي من الـ contentEditable بعد أي تعديل يدوي
      const editedHTML =
        document.getElementById("previewEditable")?.innerHTML || previewHTML;

      const res = await api.post(
        `/templates/${id}/generate`,
        {
          data: formData,
          customEditedHTML: editedHTML, // ✅ يضمن Preview = PDF 100%
        },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${template.name || "contract"}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("❌ فشل إنشاء ملف PDF");
    } finally {
      setLoading(false);
    }
  };

  // 🤖 إنشاء عقد بالذكاء الصناعي
  const generateAIContract = async () => {
    if (!aiPrompt.trim()) return alert("⚠️ اكتب وصف العقد");

    setAiLoading(true);

    try {
      // 1) generate bodyText
      const res = await api.post("/templates/generate-contract", {
        prompt: aiPrompt,
      });

      const bodyText = res.data.bodyText || "";
      const vars = extractVariables(bodyText);

      // 2) save template
      const saveRes = await api.post("/templates", {
        name: "عقد منشأ بالذكاء الصناعي",
        description: "تم توليده تلقائيًا",
        bodyText,
        fields: vars.map((v) => ({ key: v, label: v, type: "text" })),
      });

      // 3) set state
      setTemplate(saveRes.data);
      setVariables(vars);

      const initData = {};
      vars.forEach((v) => (initData[v] = ""));
      setFormData(initData);

      setShowPreview(false);
      setPreviewHTML("");

      alert("✅ تم إنشاء العقد بنجاح");
    } catch (err) {
      console.error(err);
      alert("❌ فشل إنشاء العقد");
    } finally {
      setAiLoading(false);
    }
  };

  if (!template) {
    return <p className="text-center text-white mt-20">جاري التحميل…</p>;
  }

  return (
    <div className="p-6 sm:p-8 bg-gray-900 text-white min-h-screen">
      <motion.h1
        initial={{ opacity: 0, y: -25 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55 }}
        className="text-3xl font-extrabold text-center mb-8 text-yellow-400"
      >
        ⚖️ إنشاء عقد: {template.name}
      </motion.h1>

      {/* 🤖 AI Section */}
      <div className="bg-gray-800 p-6 rounded-2xl max-w-3xl mx-auto mb-10 shadow-xl">
        <h2 className="text-lg font-bold mb-3 text-purple-300">🤖 توليد عقد بالذكاء الصناعي</h2>

        <textarea
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
          className="w-full p-3 rounded text-black mb-3 outline-none"
          placeholder="اكتب وصف العقد المطلوب..."
        />

        <button
          onClick={generateAIContract}
          disabled={aiLoading}
          className="w-full bg-purple-600 hover:bg-purple-500 text-white p-3 rounded-xl font-bold disabled:opacity-60"
        >
          {aiLoading ? "⏳ جاري التوليد..." : (
            <>
              <FaRobot className="inline mx-1" /> توليد عقد بالذكاء الصناعي
            </>
          )}
        </button>
      </div>

      {/* 🧾 Variables */}
      <div className="bg-gray-800 p-6 rounded-2xl max-w-3xl mx-auto shadow-xl">
        <h2 className="text-lg font-bold mb-3 text-blue-200">🧩 المتغيرات</h2>

        {variables.length === 0 && (
          <p className="text-gray-300 text-sm mb-3">
            لا توجد متغيرات داخل النموذج.
          </p>
        )}

        {variables.map((v) => (
          <input
            key={v}
            placeholder={v}
            value={formData[v] || ""}
            onChange={(e) => handleChange(v, e.target.value)}
            className="w-full p-3 rounded text-black mb-3 outline-none"
          />
        ))}

        <button
          onClick={addVariable}
          className="w-full bg-blue-500 hover:bg-blue-400 p-3 rounded-xl text-black font-bold mt-2"
        >
          <FaPlusCircle className="inline mx-1" /> إضافة متغير
        </button>

        <button
          onClick={previewContract}
          className="w-full bg-yellow-400 hover:bg-yellow-300 p-3 rounded-xl text-black font-bold mt-3"
        >
          <FaEye className="inline mx-1" /> معاينة العقد
        </button>
      </div>

      {/* ✅ Preview */}
      {showPreview && (
        <div
          id="previewSection"
          className="bg-white text-black p-4 sm:p-8 max-w-6xl mx-auto mt-12 rounded-2xl shadow-2xl"
        >
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-between mb-4">
            <h3 className="font-extrabold text-lg text-gray-800">
              📄 المعاينة (تعديل مباشر)
            </h3>

            <button
              onClick={() => setShowPreview(false)}
              className="bg-gray-200 hover:bg-gray-300 text-gray-900 font-bold py-2 px-4 rounded-xl"
            >
              <FaTimes className="inline mx-1" />
              إغلاق
            </button>
          </div>

          {/* ✅ editable preview */}
          <div
            id="previewEditable"
            contentEditable
            suppressContentEditableWarning
            dangerouslySetInnerHTML={{ __html: previewHTML }}
            style={{
              minHeight: "60vh",
              overflow: "auto",
              border: "1px solid #ddd",
              borderRadius: "14px",
              padding: "12px",
              outline: "none",
              direction: "rtl",
              fontFamily: "Cairo, sans-serif",
            }}
          />

          <p className="text-sm text-gray-600 mt-3">
            ✅ تقدر تعدّل النص هنا مباشرة، والطباعة هتطلع نفس اللي قدامك بالظبط.
          </p>

          <button
            onClick={generatePDF}
            disabled={loading}
            className="mt-6 w-full bg-green-500 hover:bg-green-400 p-3 rounded-xl text-black font-bold disabled:opacity-60"
          >
            {loading ? "⏳ جاري التحميل..." : (
              <>
                <FaPrint className="inline mx-1" /> تحميل PDF
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default TemplateForm;
